"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useSetupChecklist } from "@/hooks/useSetupChecklist";
import { SetupChecklistItem, getSkippedItems, saveSkippedItems, SKIPPED_ITEMS_KEY } from "@/components/presentational/tailwind/SetupChecklist";
import { cn } from "@/lib/utils";
import { useDemoContext } from "@/contexts/DemoContext";
import { motion, useMotionValue, useAnimationFrame, useSpring } from "framer-motion";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { useQueryClient } from "@tanstack/react-query";
import { requestStartGrowth } from "@/utils/start-growth-client";
import { useConfettiContext } from "@/contexts/ConfettiContextProvider";
import { toast } from "sonner";
import { useGetAllTargetsForAccount } from "@/hooks/queries/targets";
import { useNotificationStatus } from "@/hooks/queries/notifications";

// "add-targets" and "enable-reports" can be skipped
const SKIPPABLE_ITEMS = new Set(["add-targets", "enable-reports"]);
interface SetupCompletionCardProps {
  accountId: string | null;
  accountData?: any;
  className?: string;
  /** When provided, clicking "Add targets" scrolls to this element ID instead of navigating */
  scrollToTargetId?: string;
}
export default function SetupCompletionCard(t0) {
  const $ = _c(57);
  const {
    accountId,
    accountData,
    className,
    scrollToTargetId
  } = t0;
  const demoContext = useDemoContext();
  const {
    data: targets,
    isLoading: isLoadingTargets
  } = useGetAllTargetsForAccount(accountId);
  const [isHovered, setIsHovered] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defer: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: notificationData
  } = useNotificationStatus(t1);
  let t2;
  if ($[1] !== notificationData?.channels) {
    t2 = notificationData?.channels?.some(_temp) ?? false;
    $[1] = notificationData?.channels;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const hasVerifiedChannel = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [skippedIds, setSkippedIds] = useState(t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSkippedIds(getSkippedItems());
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const handleStorageChange = e => {
        if (e.key === SKIPPED_ITEMS_KEY) {
          setSkippedIds(getSkippedItems());
        }
      };
      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    };
    t7 = [];
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = itemId => {
      setSkippedIds(prev => {
        const newSkipped = new Set(prev);
        newSkipped.add(itemId);
        saveSkippedItems(newSkipped);
        return newSkipped;
      });
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const handleSkip = t8;
  const {
    openUpgradeModal
  } = useModalContext();
  const queryClient = useQueryClient();
  const {
    setShowConfetti
  } = useConfettiContext();
  let t9;
  if ($[9] !== accountData?.account_type || $[10] !== accountId || $[11] !== openUpgradeModal || $[12] !== queryClient || $[13] !== setShowConfetti) {
    t9 = () => {
      if (!accountId) {
        return;
      }
      if (accountData?.account_type === "trial") {
        openUpgradeModal(accountId);
      } else {
        requestStartGrowth(accountId).then(result => {
          if (result.ok) {
            toast.success("Growth started successfully!");
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 3000);
          } else {
            toast.error(result.message);
          }
          queryClient.invalidateQueries(["account", accountId]);
          queryClient.invalidateQueries(["all-accounts"]);
        });
      }
    };
    $[9] = accountData?.account_type;
    $[10] = accountId;
    $[11] = openUpgradeModal;
    $[12] = queryClient;
    $[13] = setShowConfetti;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  accountData?.account_type;
  const handleStartGrowth = t9;
  let t10;
  if ($[15] !== scrollToTargetId) {
    t10 = () => {
      if (!scrollToTargetId) {
        return;
      }
      const element = document.getElementById(scrollToTargetId);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    };
    $[15] = scrollToTargetId;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const handleAddTargets = t10;
  const rotation = useMotionValue(0);
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      current: 0.15,
      target: 0.15
    };
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const velocityRef = useRef(t11);
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      stiffness: 80,
      damping: 20
    };
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const overlayOpacity = useSpring(1, t12);
  let t13;
  let t14;
  if ($[19] !== isHovered || $[20] !== overlayOpacity) {
    t13 = () => {
      velocityRef.current.target = isHovered ? 1.2 : 0.15;
      overlayOpacity.set(isHovered ? 0.1 : 1);
    };
    t14 = [isHovered, overlayOpacity];
    $[19] = isHovered;
    $[20] = overlayOpacity;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  let t15;
  if ($[23] !== rotation) {
    t15 = (_, delta) => {
      const {
        current,
        target
      } = velocityRef.current;
      const newVelocity = current + (target - current) * 0.03;
      velocityRef.current.current = newVelocity;
      const frameMultiplier = delta / 16.67;
      rotation.set((rotation.get() + newVelocity * frameMultiplier) % 360);
    };
    $[23] = rotation;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  useAnimationFrame(t15);
  let t16;
  if ($[25] !== targets) {
    t16 = targets || [];
    $[25] = targets;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = accountData?.active;
  const t18 = accountData?.account_type !== "trial";
  let t19;
  if ($[27] !== accountData || $[28] !== hasVerifiedChannel || $[29] !== t16 || $[30] !== t17 || $[31] !== t18) {
    t19 = {
      ...accountData,
      targets: t16,
      is_running: t17,
      has_subscription: t18,
      has_verified_channel: hasVerifiedChannel
    };
    $[27] = accountData;
    $[28] = hasVerifiedChannel;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const checklistData = t19;
  const t20 = scrollToTargetId ? handleAddTargets : undefined;
  let t21;
  if ($[33] !== handleStartGrowth || $[34] !== t20) {
    t21 = {
      onStartGrowth: handleStartGrowth,
      onAddTargets: t20
    };
    $[33] = handleStartGrowth;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const items = useSetupChecklist(accountId, checklistData, t21);
  const completed = items.filter(i => i.completed || skippedIds.has(i.id)).length;
  const total = items.length;
  const {
    completedCount,
    totalCount
  } = {
    completedCount: completed,
    totalCount: total
  };
  if (demoContext?.isDemoMode) {
    return null;
  }
  if (accountData?.active === true) {
    return null;
  }
  if (isLoadingTargets) {
    return null;
  }
  if (completedCount === totalCount && totalCount > 0) {
    return null;
  }
  if (items.length === 0) {
    return null;
  }
  let t22;
  if ($[36] !== className) {
    t22 = cn("relative mb-6 overflow-hidden rounded-2xl shadow-sm ring-1 ring-gray-200/50", className);
    $[36] = className;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  let t24;
  let t25;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      padding: "4px",
      background: "#E8D4F0"
    };
    t24 = () => setIsHovered(true);
    t25 = () => setIsHovered(false);
    $[38] = t23;
    $[39] = t24;
    $[40] = t25;
  } else {
    t23 = $[38];
    t24 = $[39];
    t25 = $[40];
  }
  let t26;
  if ($[41] !== rotation) {
    t26 = <motion.div className="absolute left-1/2 top-1/2 z-0 rounded-full" style={{
      width: "max(300%, 1200px)",
      aspectRatio: "1",
      background: "conic-gradient(from 230deg at 50% 50%, #FD7336 0deg, #D7A7FF 90deg, #AFFAFF 180deg, #D7A7FF 270deg, #FD7336 360deg)",
      x: "-50%",
      y: "-50%",
      rotate: rotation
    }} />;
    $[41] = rotation;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== overlayOpacity) {
    t27 = <motion.div className="absolute inset-0 z-1" style={{
      background: "linear-gradient(0deg, rgba(243, 244, 246, 0.00) 0%, rgba(243, 244, 246, 0.04) 62.8%, #F3F4F6 100%), linear-gradient(270deg, rgba(243, 244, 246, 0.00) 32.65%, #F3F4F6 93.63%)",
      opacity: overlayOpacity
    }} />;
    $[43] = overlayOpacity;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="pointer-events-none absolute -right-8 -top-4 hidden opacity-[0.04] md:block" aria-hidden="true"><svg width="200" height="200" viewBox="25 25 108 112" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M123.226 90c0 24.55-20.069 44.45-44.827 44.45S33.572 114.55 33.572 90c0-24.6 20.07-44.5 44.827-44.5 24.758 0 44.827 19.9 44.827 44.5Zm-20.018-.25c0-13.6-11.144-24.65-24.91-24.65-13.715 0-24.859 11.05-24.859 24.65 0 13.65 11.144 24.7 24.86 24.7 13.765 0 24.909-11.05 24.909-24.7Z" /><path fill="currentColor" d="M35.186 52.75c-8.32-4.3-10.69-8.5-8.169-14.1 2.32-5 11.95-9.25 26.422-11.6 32.776-5.4 70.241.45 76.241 11.9 1.21 2.4.857 6.4-.807 8.4-2.067 2.55-6.504 5.5-10.084 6.7l-3.429 1.15-1.16-1c-1.916-1.7-7.866-5.7-8.421-5.7-1.412 0-.353-1 1.362-1.3 4.538-.75 12.253-3.6 12.606-4.6.1-.35-1.412-1.35-3.429-2.3-13.11-6.25-44.323-7.8-65.5-3.25-5.598 1.2-12.859 3.7-13.665 4.7-1.06 1.25 1.966 2.6 13.564 6.05.352.1-1.009 1.3-3.026 2.65-2.067 1.35-4.286 2.95-4.992 3.5-1.765 1.45-2.723 1.3-7.513-1.2Z" /></svg></div>;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="mb-4"><h2 className="text-balance font-dashboard text-[22px] font-bold tracking-tight text-gray-900">Let's get you growing in 60 seconds</h2></div>;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== items || $[48] !== skippedIds) {
    t30 = items.map((item, index) => {
      const isSkipped = skippedIds.has(item.id);
      const isDone = item.completed || isSkipped;
      const isNext = !isDone && items.slice(0, index).every(i_0 => i_0.completed || skippedIds.has(i_0.id));
      const isActive = isNext;
      const isLast = index === items.length - 1;
      const canSkip = SKIPPABLE_ITEMS.has(item.id);
      return <StepItem key={item.id} item={item} index={index} isCompleted={isDone} isActive={isActive} isLast={isLast} canSkip={canSkip} onSkip={() => handleSkip(item.id)} />;
    });
    $[47] = items;
    $[48] = skippedIds;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== t30) {
    t31 = <div className="relative z-10 w-full overflow-hidden rounded-[11px] bg-white p-[30px]">{t28}<div className="relative z-10">{t29}<div className="relative flex flex-col">{t30}</div></div></div>;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== t22 || $[53] !== t26 || $[54] !== t27 || $[55] !== t31) {
    t32 = <div className={t22} style={t23} onMouseEnter={t24} onMouseLeave={t25}>{t26}{t27}{t31}</div>;
    $[52] = t22;
    $[53] = t26;
    $[54] = t27;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  return t32;
}
function _temp(c) {
  return c.is_verified;
}
interface StepItemProps {
  item: SetupChecklistItem;
  index: number;
  isCompleted: boolean;
  isActive: boolean;
  isLast: boolean;
  canSkip: boolean;
  onSkip: () => void;
}
function StepItem(t0) {
  const $ = _c(30);
  const {
    item,
    index,
    isCompleted,
    isActive,
    isLast,
    canSkip,
    onSkip
  } = t0;
  const t1 = !isLast && (isActive ? "pb-6" : "pb-3");
  let t2;
  if ($[0] !== t1) {
    t2 = cn("relative flex gap-4", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isCompleted || $[3] !== isLast) {
    t3 = !isLast && <div className={cn("absolute left-4 top-4 h-full w-[2px] -translate-x-1/2", isCompleted ? "bg-green-500" : "bg-gray-100")} />;
    $[2] = isCompleted;
    $[3] = isLast;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== index || $[6] !== isActive || $[7] !== isCompleted) {
    t4 = <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">{isCompleted ? <div className="flex h-full w-full items-center justify-center rounded-full bg-green-500 text-white shadow-sm ring-2 ring-green-100"><svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></div> : isActive ? <div className="flex h-full w-full items-center justify-center rounded-full bg-gray-900 text-sm font-bold text-white shadow-sm ring-2 ring-gray-100">{index + 1}</div> : <div className="flex h-full w-full items-center justify-center rounded-full bg-gray-50 text-sm font-semibold text-gray-400">{index + 1}</div>}</div>;
    $[5] = index;
    $[6] = isActive;
    $[7] = isCompleted;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = isActive ? "w-full" : "";
  let t6;
  if ($[9] !== t5) {
    t6 = cn("flex flex-col pt-2", t5);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = isCompleted ? "text-gray-500" : isActive ? "text-gray-900" : "text-gray-400";
  let t8;
  if ($[11] !== t7) {
    t8 = cn("text-base font-semibold leading-none", t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== item.title || $[14] !== t8) {
    t9 = <h3 className={t8}>{item.title}</h3>;
    $[13] = item.title;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== canSkip || $[17] !== isActive || $[18] !== item || $[19] !== onSkip) {
    t10 = isActive && <div className="mt-2 duration-300 animate-in fade-in slide-in-from-top-1"><p className="mb-3 max-w-xl text-[14px] leading-relaxed text-gray-500">{item.description}</p><div className="flex flex-wrap items-center gap-3"><a href={item.actionHref || "#"} onClick={e => {
          if (item.onAction) {
            e.preventDefault();
            item.onAction();
          }
        }} className="inline-flex h-10 items-center justify-center rounded-xl bg-black px-6 text-sm font-semibold text-white transition-all hover:bg-gray-800 hover:ring-4 hover:ring-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.98]">{item.actionLabel}</a>{canSkip && <button onClick={onSkip} className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm font-medium text-gray-500 transition-colors hover:text-gray-700">Skip</button>}</div></div>;
    $[16] = canSkip;
    $[17] = isActive;
    $[18] = item;
    $[19] = onSkip;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6 || $[23] !== t9) {
    t11 = <div className={t6}>{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t2 || $[27] !== t3 || $[28] !== t4) {
    t12 = <div className={t2}>{t3}{t4}{t11}</div>;
    $[25] = t11;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}